import cx from 'classnames'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDisableScroll } from 'hooks'

import { unWei } from 'lib/helpers'
import { LocalStorage } from 'lib/storage'
import { snowplowEvent, logEvent } from 'lib/analytics'
import { useModal } from 'store/slices/modals'
import { Post } from 'lib/_types'
import {
  Gift,
  GiftRecipient,
  GiftSource,
  GiftTargetObject,
} from 'lib/_types/gift'

import { CallUser } from 'store/slices/agora'
import { useCurrentUser } from 'store/api/yay/user'

import useConferenceCallFunctions from 'features/ConferenceCall/core/hooks/useConferenceCallFunctions'

import SendGift from 'components/SendGift/SendGift'
import TokenDisplay from 'components/Token/TokenDisplay'
import { useEMPLDetails } from 'features/Bag/useBagDetails'
import GiftAnimation from '../GiftAnimationModal/GiftAnimation'
import withOTPVerification from '../OTPModal/withOTPVerification'

type SendGiftData = {
  source: GiftSource
  targetObj: GiftTargetObject
  recipients: Array<GiftRecipient>
}

export const useSendGiftModal = () => useModal<SendGiftData>('SendGiftModal')

const _SendGiftModal = ({ giftBoxModal, handleSendGift }) => {
  const { t } = useTranslation()

  const emplDetailsRes = useEMPLDetails()

  const onClose = giftBoxModal.onClose
  const budget = unWei(Number(emplDetailsRes?.data?.empl?.total))

  const [sentGift, setSentGift] = useState<Gift | null>()

  const onSend = (sentData: {
    sentGift: { gift: Gift; num: number }
    recipient: GiftRecipient
  }) => {
    setSentGift(sentData.sentGift?.gift)
    setTimeout(() => {
      handleSendGift(sentData)
      onClose()
    }, 3000)
  }

  useEffect(() => {
    if (!giftBoxModal.isActive) {
      setSentGift(null)
    }
  }, [giftBoxModal])

  return (
    <div
      className={cx('Modal SendGiftModal', {
        'Modal--active': giftBoxModal.isActive,
      })}
    >
      <a className="Modal__overlay" onClick={() => onClose()} />

      {giftBoxModal.isActive && !!sentGift && (
        <GiftAnimation
          giftId={sentGift?.id}
          giftImg={sentGift?.icon}
          giftName={sentGift?.title}
          type={'sent'}
        />
      )}

      {giftBoxModal.isActive && !sentGift && (
        <div className="Modal__wrapper Modal__wrapper--auto-h">
          <div className="Modal__header Modal__header--bdrz-t">
            <a className="Modal__header__close" onClick={() => onClose()} />

            <h2 className="Modal__header__h2">{t('gift.send')}</h2>

            <TokenDisplay type="empl">{budget.toLocaleString()}</TokenDisplay>
          </div>
          <div className="Modal__body">
            <SendGift
              giftType={giftBoxModal.data.source}
              giftTarget={giftBoxModal.data.targetObj}
              recipients={giftBoxModal.data.recipients}
              budget={budget}
              onSend={onSend}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const _SendGiftPost = ({ giftBoxModal }) => {
  const handleSendGift = (sentData: {
    sentGift: { gift: Gift; num: number }
    recipient: GiftRecipient
  }) => {
    snowplowEvent.gift_post({
      post: {
        post_id: (giftBoxModal.data.targetObj as Post)?.id,
        author_id: (giftBoxModal.data.targetObj as Post)?.user?.id,
        post_type: (giftBoxModal.data.targetObj as Post)?.post_type,
        location: giftBoxModal.data.source === 'post' ? 'timeline' : 'group',
      },
    })
  }

  return (
    <_SendGiftModal
      giftBoxModal={giftBoxModal}
      handleSendGift={handleSendGift}
    />
  )
}

const _SendGiftUser = ({ giftBoxModal }) => {
  const handleSendGift = (sentData: {
    sentGift: { gift: Gift; num: number }
    recipient: GiftRecipient
  }) => {
    return
  }

  return (
    <_SendGiftModal
      giftBoxModal={giftBoxModal}
      handleSendGift={handleSendGift}
    />
  )
}

const _SendGiftGroup = ({ giftBoxModal }) => {
  const { data: currentUser } = useCurrentUser()
  const handleSendGift = (sentData: {
    sentGift: { gift: Gift; num: number }
    recipient: GiftRecipient
  }) => {
    logEvent.gift_send({
      from: 'group',
      from_id: currentUser?.id,
      gift_id: sentData.sentGift?.gift.id,
      receiver_user_id: Number(sentData?.recipient?.id),
    })
  }

  return (
    <_SendGiftModal
      giftBoxModal={giftBoxModal}
      handleSendGift={handleSendGift}
    />
  )
}

const _SendGiftCall = ({ giftBoxModal }) => {
  const { data: currentUser } = useCurrentUser()
  const conferenceCallFunctions = useConferenceCallFunctions()

  const handleSendGift = (sentData: {
    sentGift: { gift: Gift; num: number }
    recipient: GiftRecipient
  }) => {
    conferenceCallFunctions.sendGift(
      `gift ${(sentData.recipient as CallUser)?.uid} ${
        sentData.sentGift?.gift.id
      } ${sentData.sentGift?.num}`
    )
    logEvent.gift_send({
      from: 'call',
      from_id: currentUser?.id,
      gift_id: sentData.sentGift?.gift.id,
      receiver_user_id: Number(sentData?.recipient?.id),
      call_duration_seconds: LocalStorage.callDuration.get(0),
    })
  }

  return (
    <_SendGiftModal
      giftBoxModal={giftBoxModal}
      handleSendGift={handleSendGift}
    />
  )
}

const SendGiftModal = () => {
  const giftBoxModal = useSendGiftModal()
  const giftSource = giftBoxModal?.data?.source
  useDisableScroll(giftBoxModal.isActive)

  return (
    <>
      {giftSource === 'call' && <_SendGiftCall giftBoxModal={giftBoxModal} />}
      {(giftSource === 'post' || giftSource === 'circle_post') && (
        <_SendGiftPost giftBoxModal={giftBoxModal} />
      )}
      {giftSource === 'circle' && (
        <_SendGiftGroup giftBoxModal={giftBoxModal} />
      )}
      {giftSource === 'user_profile' && (
        <_SendGiftUser giftBoxModal={giftBoxModal} />
      )}
    </>
  )
}

export default withOTPVerification(SendGiftModal, useSendGiftModal)
